/**
 * Payment.
 *
 * The one screen where a mistake costs real money, so three rules are enforced
 * in the UI and not left to the API:
 *
 *  1. NEVER QUEUED OFFLINE. `payment.receive` is on the outbox deny list
 *     (`NEVER_QUEUE_OFFLINE` in @sonauto/offline-sync): the client cannot know
 *     the current outstanding balance, and replaying a payment an hour later
 *     against a bill someone else already settled double-charges the guest.
 *     Offline, the payment buttons are disabled with that explanation.
 *  2. CHANGE IS SERVER-COMPUTED. The keypad shows a local preview so the
 *     cashier can count notes, but the figure handed to the guest is
 *     `changeMinor` from the API response.
 *  3. SPLIT PAYMENT IS N CALLS. There is no batch endpoint; each method is its
 *     own `POST /v1/payments` with its own Idempotency-Key and a partial
 *     amount. The list below is the record of what has actually landed —
 *     `outstandingAfterMinor` from each response drives the next step.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Input,
  Modal,
  MoneyText,
  NumericKeypad,
  Select,
  keypadToMinor,
  useToast,
} from '@sonauto/ui';
import { useApi } from '../../auth/AuthProvider';
import { useOutbox } from '../../offline/OutboxProvider';
import { errorMessage } from '../../lib/hooks';
import { vnd } from '../../lib/format';
import type { CreatePaymentResponse, PaymentMethod, SessionDetailResponse } from '../../lib/types';

const METHODS: ReadonlyArray<{ value: PaymentMethod; label: string; needsReference: boolean }> = [
  { value: 'CASH', label: 'Tiền mặt', needsReference: false },
  { value: 'BANK_TRANSFER', label: 'Chuyển khoản', needsReference: true },
  { value: 'QR_BANK', label: 'QR ngân hàng', needsReference: true },
  { value: 'CARD', label: 'Thẻ', needsReference: true },
  { value: 'EWALLET', label: 'Ví điện tử', needsReference: true },
];

/** Quick-tender notes a Vietnamese cashier actually holds. */
const QUICK_CASH = [
  { label: '50k', value: '50000' },
  { label: '100k', value: '100000' },
  { label: '200k', value: '200000' },
  { label: '500k', value: '500000' },
];

export interface PaymentScreenProps {
  readonly branchId: string;
  readonly detail: SessionDetailResponse;
  readonly cashSessionId: string | null;
  readonly onClose: () => void;
  readonly onPaid: () => void;
}

export function PaymentScreen({
  branchId,
  detail,
  cashSessionId,
  onClose,
  onPaid,
}: PaymentScreenProps): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const outbox = useOutbox();

  const exponent = detail.orders[0]?.currency_exponent ?? 0;
  const currency = detail.orders[0]?.currency ?? 'VND';

  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [amountText, setAmountText] = useState('');
  const [tenderedText, setTenderedText] = useState('');
  const [reference, setReference] = useState('');
  const [tipText, setTipText] = useState('');
  const [taken, setTaken] = useState<readonly CreatePaymentResponse[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const outstanding = useMemo(() => {
    const last = taken.at(-1);
    return last === undefined ? detail.balance.outstandingMinor : last.outstandingAfterMinor;
  }, [detail.balance.outstandingMinor, taken]);

  const amountMinor = amountText === '' ? outstanding : keypadToMinor(amountText, exponent);
  const tenderedMinor = keypadToMinor(tenderedText, exponent);
  const tipMinor = keypadToMinor(tipText, exponent);
  // LOCAL PREVIEW ONLY. The figure given to the guest is the API's changeMinor.
  const changePreview = Math.max(0, tenderedMinor - (amountMinor + tipMinor));

  const online = outbox.snapshot.online;

  const take = async (): Promise<void> => {
    setError(null);
    if (amountMinor <= 0) {
      setError('Số tiền phải lớn hơn 0.');
      return;
    }
    if (amountMinor > outstanding) {
      setError('Số tiền vượt quá số còn phải thu. Phần dư hãy nhập vào ô "Tiền tip".');
      return;
    }
    if (method === 'CASH' && cashSessionId === null) {
      setError('Thu tiền mặt phải gắn với một ca quỹ đang mở. Hãy mở ca quỹ trước.');
      return;
    }
    if (method === 'CASH' && tenderedMinor < amountMinor + tipMinor) {
      setError('Tiền khách đưa nhỏ hơn số tiền thu.');
      return;
    }

    setBusy(true);
    try {
      const response = await api.payments.create({
        branchId,
        diningSessionId: detail.session.id,
        method,
        amountMinor,
        tenderedMinor: method === 'CASH' ? tenderedMinor : 0,
        tipMinor,
        referenceNo: reference.trim() === '' ? null : reference.trim(),
        cashRegisterSessionId: method === 'CASH' ? cashSessionId : null,
      });
      setTaken([...taken, response]);
      setAmountText('');
      setTenderedText('');
      setTipText('');
      setReference('');
      toast.success(
        `Đã thu ${vnd(response.amountMinor, currency, exponent)}`,
        response.changeMinor > 0 ? `Trả lại khách ${vnd(response.changeMinor, currency, exponent)}` : undefined,
      );
      if (response.outstandingAfterMinor <= 0) onPaid();
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Thanh toán"
      description={`Bàn ${detail.session.session_code} · còn lại ${vnd(outstanding, currency, exponent)}`}
      size="xl"
      footer={
        <div className="flex w-full items-center gap-2">
          <div className="text-sm text-fg-muted">
            Đã thu {taken.length} lượt ·{' '}
            <MoneyText
              amountMinor={taken.reduce((a, p) => a + p.amountMinor, 0)}
              currency={currency}
              exponent={exponent}
            />
          </div>
          <Button variant="secondary" className="ml-auto" onClick={onClose}>
            Đóng
          </Button>
        </div>
      }
    >
      {!online ? (
        <div className="mb-3 rounded-md bg-danger-bg p-3 text-sm text-danger-fg ring-1 ring-danger-solid/40">
          <b>Đang ngoại tuyến — không thể thu tiền.</b> Thanh toán không được xếp hàng đợi vì máy không biết số dư
          thật của hoá đơn; phát lại sau có thể thu trùng của khách.
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {METHODS.map((m) => (
              <Button
                key={m.value}
                variant={method === m.value ? 'primary' : 'secondary'}
                size="lg"
                onClick={() => setMethod(m.value)}
              >
                {m.label}
              </Button>
            ))}
          </div>

          <Card title="Số tiền thu" padded>
            <NumericKeypad
              value={amountText}
              onChange={setAmountText}
              exponent={exponent}
              submitLabel="Ghi nhận"
              onSubmit={() => void take()}
              disabled={busy || !online}
              quickValues={[
                { label: 'Toàn bộ', value: String(outstanding) },
                ...(method === 'CASH' ? QUICK_CASH : []),
              ]}
              display={
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-fg-muted">Thu</span>
                  <MoneyText
                    amountMinor={amountMinor}
                    currency={currency}
                    exponent={exponent}
                    className="text-2xl font-bold"
                  />
                </div>
              }
            />
          </Card>
        </div>

        <div className="space-y-3">
          {method === 'CASH' ? (
            <Card title="Tiền khách đưa" padded>
              <NumericKeypad
                value={tenderedText}
                onChange={setTenderedText}
                exponent={exponent}
                submitLabel="Thu tiền"
                onSubmit={() => void take()}
                disabled={busy || !online}
                quickValues={QUICK_CASH}
                display={
                  <div className="space-y-1">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-fg-muted">Khách đưa</span>
                      <MoneyText
                        amountMinor={tenderedMinor}
                        currency={currency}
                        exponent={exponent}
                        className="text-xl font-semibold"
                      />
                    </div>
                    <div className="flex items-baseline justify-between rounded-md bg-success-bg px-2 py-1">
                      <span className="text-sm font-medium text-success-fg">Tiền thối (tạm tính)</span>
                      <MoneyText
                        amountMinor={changePreview}
                        currency={currency}
                        exponent={exponent}
                        className="text-2xl font-bold text-success-fg"
                      />
                    </div>
                  </div>
                }
              />
            </Card>
          ) : (
            <Card title="Thông tin giao dịch" padded>
              <div className="space-y-3">
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Mã giao dịch / 4 số cuối thẻ / mã QR"
                />
                {method === 'QR_BANK' || method === 'EWALLET' ? (
                  <div className="rounded-md bg-warning-bg p-3 text-sm text-warning-fg">
                    Máy chủ chưa có endpoint tạo mã QR động hay nhận webhook từ cổng thanh toán
                    (<code className="font-mono">POST /api/v1/payments/intents</code>). Hãy dùng mã QR tĩnh của quầy,
                    xác nhận đã nhận tiền rồi ghi nhận ở đây.
                  </div>
                ) : null}
                <Select
                  value={String(amountMinor)}
                  onChange={(e) => setAmountText(e.target.value)}
                  options={[
                    { value: String(outstanding), label: `Toàn bộ — ${vnd(outstanding, currency, exponent)}` },
                    { value: String(Math.round(outstanding / 2)), label: 'Một nửa' },
                  ]}
                />
                <NumericKeypad
                  value={amountText}
                  onChange={setAmountText}
                  exponent={exponent}
                  submitLabel="Ghi nhận"
                  onSubmit={() => void take()}
                  disabled={busy || !online}
                />
              </div>
            </Card>
          )}

          <Card title="Tiền tip (không trừ vào hoá đơn)" padded>
            <Input value={tipText} onChange={(e) => setTipText(e.target.value)} inputMode="numeric" placeholder="0" />
          </Card>

          {error !== null ? <ErrorState title="Không ghi nhận được" message={error} /> : null}

          <Button size="touch" fullWidth loading={busy} disabled={!online} onClick={() => void take()}>
            Ghi nhận thanh toán
          </Button>

          {taken.length > 0 ? (
            <Card title="Các lượt đã thu" padded>
              <ul className="space-y-2">
                {taken.map((payment) => (
                  <li key={payment.id} className="flex items-center justify-between gap-2 text-sm">
                    <Badge tone="success" size="sm">
                      {payment.paymentNo}
                    </Badge>
                    <span className="text-fg-muted">{payment.method}</span>
                    <MoneyText amountMinor={payment.amountMinor} currency={currency} exponent={exponent} />
                    {payment.changeMinor > 0 ? (
                      <span className="text-xs text-fg-subtle">
                        thối {vnd(payment.changeMinor, currency, exponent)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
